import React, { useState } from 'react';
import { bookingsAPI } from '../utils/api';

const REJECTION_REASONS = [
  { value: '', label: 'Select a reason...' },
  { value: 'time_unavailable', label: 'Time slot no longer available' },
  { value: 'double_booked', label: 'Double booking conflict' },
  { value: 'closed', label: 'Salon closed on this date' },
  { value: 'staff_unavailable', label: 'Staff member unavailable' },
  { value: 'other', label: 'Other reason' }
];

function RejectBookingModal({ booking, isOpen, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('time_unavailable');
  const [customReason, setCustomReason] = useState('');
  const [alternativeDate, setAlternativeDate] = useState('');
  const [alternativeTime, setAlternativeTime] = useState('');
  const [error, setError] = useState(null);

  if (!isOpen || !booking) return null;

  const handleReject = async () => {
    setLoading(true);
    setError(null);

    try {
      const reasonText = reason === 'other' ? customReason : REJECTION_REASONS.find(r => r.value === reason)?.label;

      await bookingsAPI.rejectBooking(booking.id, {
        reason: reasonText,
        alternativeDate: alternativeDate || null,
        alternativeTime: alternativeTime || null
      });

      onSuccess?.();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reject booking');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setReason('time_unavailable');
    setCustomReason('');
    setAlternativeDate('');
    setAlternativeTime('');
    setError(null);
    onClose();
  };

  // Get tomorrow's date for the min date
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  return (
    <div
      className="modal-overlay"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 2000 }}
      onClick={handleClose}
    >
      <div
        className="modal-content"
        style={{ maxWidth: '500px', overflow: 'hidden' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="modal-header"
          style={{
            padding: '1.25rem 1.5rem',
            background: '#fef2f2'
          }}
        >
          <h3 style={{
            margin: 0,
            fontSize: '1.125rem',
            fontWeight: '600',
            color: '#991b1b',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            Reject Booking
          </h3>
        </div>

        {/* Body */}
        <div className="modal-body">
          {/* Booking Details */}
          <div style={{
            background: '#f8fafc',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.25rem'
          }}>
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280' }}>Customer</span>
                <span style={{ fontWeight: '500' }}>{booking.customer_name}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280' }}>Service</span>
                <span style={{ fontWeight: '500' }}>{booking.service_name}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280' }}>Requested Date</span>
                <span style={{ fontWeight: '500' }}>{booking.booking_date}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280' }}>Requested Time</span>
                <span style={{ fontWeight: '500' }}>{booking.start_time}</span>
              </div>
            </div>
          </div>

          {/* Rejection Reason */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
              Reason for Rejection
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                fontSize: '0.95rem',
                background: 'white'
              }}
            >
              {REJECTION_REASONS.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>

            {reason === 'other' && (
              <input
                type="text"
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Enter reason..."
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  fontSize: '0.95rem',
                  marginTop: '0.5rem',
                  boxSizing: 'border-box'
                }}
              />
            )}
          </div>

          {/* Alternative Suggestion */}
          <div style={{
            background: '#eff6ff',
            borderRadius: '8px',
            padding: '1rem',
            border: '1px solid #bfdbfe'
          }}>
            <p style={{
              margin: '0 0 0.75rem',
              fontWeight: '500',
              color: '#1e40af',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              💡 Suggest Alternative (Optional)
            </p>
            <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: '#4b5563' }}>
              Help the customer by suggesting another available time.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: '#4b5563' }}>
                  Date
                </label>
                <input
                  type="date"
                  value={alternativeDate}
                  onChange={(e) => setAlternativeDate(e.target.value)}
                  min={minDate}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '0.95rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', color: '#4b5563' }}>
                  Time
                </label>
                <input
                  type="time"
                  value={alternativeTime}
                  onChange={(e) => setAlternativeTime(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '0.95rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>
          </div>

          {error && (
            <div style={{
              marginTop: '1rem',
              padding: '0.75rem 1rem',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#dc2626',
              fontSize: '0.875rem'
            }}>
              {error}
            </div>
          )}

          <p style={{
            margin: '1rem 0 0',
            color: '#6b7280',
            fontSize: '0.8rem',
            lineHeight: '1.5'
          }}>
            The customer will be notified by email about this rejection
            {alternativeDate && alternativeTime ? ' and the suggested alternative time' : ''}.
          </p>
        </div>

        {/* Footer */}
        <div
          className="modal-footer"
          style={{ background: '#f9fafb' }}
        >
          <button
            onClick={handleClose}
            disabled={loading}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleReject}
            disabled={loading || (!reason && !customReason)}
            className="btn btn-danger"
          >
            {loading ? 'Rejecting...' : 'Reject Booking'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RejectBookingModal;
